import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Database, RefreshCw, ShieldAlert, ShieldCheck, TriangleAlert,
  Wrench, RotateCcw, ChevronRight, Trash2, ScanSearch,
} from "lucide-react";

const STATUS = {
  ok:    { label: "Healthy",  cls: "bg-emerald-50 text-emerald-800 border-emerald-300", icon: ShieldCheck },
  warn:  { label: "Dupes",    cls: "bg-amber-50 text-amber-800 border-amber-300",       icon: TriangleAlert },
  fail:  { label: "Orphans",  cls: "bg-rose-50 text-rose-800 border-rose-300",          icon: ShieldAlert },
};

function statusOf(check) {
  if ((check.orphans || 0) > 0) return "fail";
  if ((check.dupe_group_count || 0) > 0) return "warn";
  return "ok";
}

export default function AdminDataIntegrity() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  // Feb 2026 · Bulk-dedupe state. Two-step flow: preview → confirm → execute.
  const [dedupePreview, setDedupePreview] = useState(null);
  const [dedupeLoading, setDedupeLoading] = useState(false);
  const [dedupeRunning, setDedupeRunning] = useState(false);
  const [dedupeResult, setDedupeResult] = useState(null);

  const loadDedupePreview = async () => {
    setDedupeLoading(true); setDedupeResult(null);
    try {
      const r = await api.get(`/admin/data-integrity/dedupe-preview`);
      setDedupePreview(r.data);
      const n = r.data?.total_rows_to_remove || 0;
      toast.success(n === 0
        ? "No duplicate rows found — you're clean."
        : `Preview: ${n.toLocaleString()} duplicate row(s) would be removed`);
    } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
    finally { setDedupeLoading(false); }
  };

  const executeDedupe = async () => {
    const n = dedupePreview?.total_rows_to_remove || 0;
    if (n === 0) { toast.info("Nothing to dedupe"); return; }
    if (!window.confirm(`Permanently remove ${n.toLocaleString()} duplicate row(s) across ${dedupePreview.collections.length} collection(s)? Winners are picked by most-recent updated_at. This can't be undone.`)) return;
    const typed = window.prompt("Type DEDUPE to confirm:");
    if (typed !== "DEDUPE") { toast.error("Cancelled"); return; }
    setDedupeRunning(true);
    try {
      const r = await api.post(`/admin/data-integrity/dedupe`);
      setDedupeResult(r.data);
      setDedupePreview(null);
      toast.success(`Removed ${(r.data.total_removed || 0).toLocaleString()} row(s). Restart backend so unique indexes can build.`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Dedupe failed")); }
    finally { setDedupeRunning(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/data-integrity/scan`);
      setData(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Scan failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runFinanceDedupe = async (wsId, code) => {
    if (!wsId) return;
    if (!window.confirm(`Dedupe finance_accounts for workspace ${wsId}? Non-destructive: repoints journal_lines to keeper and soft-deletes the losers.`)) return;
    setBusyKey(`dedupe-${wsId}`);
    try {
      const r = await api.post(`/finance/workspaces/${wsId}/accounts/dedupe?dry_run=false`);
      toast.success(`Merged ${r.data.duplicate_groups} group(s) in ${wsId.slice(0, 8)}…`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Dedupe failed")); }
    finally { setBusyKey(null); }
  };

  const resetWorkspace = async (wsId, reseed) => {
    if (!window.confirm(`RESET workspace ${wsId}? This wipes ALL scoped data (brands, projects, invoices, finance…) but keeps the workspace + members. Type OK to continue.`)) return;
    const typed = window.prompt("Type RESET to confirm:");
    if (typed !== "RESET") { toast.error("Cancelled"); return; }
    setBusyKey(`reset-${wsId}`);
    try {
      const r = await api.post(`/workspaces/${wsId}/reset?confirm=RESET&reseed=${reseed ? "true" : "false"}`);
      const swept = Object.values(r.data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Reset ${wsId.slice(0, 8)}… wiped ${swept} rows`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Reset failed")); }
    finally { setBusyKey(null); }
  };

  const checks = data?.checks || {};
  const collectionNames = Object.keys(checks);
  const totalOrphans = collectionNames.reduce((s, c) => s + (checks[c].orphans || 0), 0);
  const totalDupeRows = collectionNames.reduce((s, c) => s + (checks[c].dupe_row_count || 0), 0);
  const overallStatus = totalOrphans > 0 ? "fail" : totalDupeRows > 0 ? "warn" : "ok";
  const overall = STATUS[overallStatus];

  return (
    <>
      <PageHeader
        eyebrow="Super Admin"
        title="Data Integrity"
        description="Cross-collection scan for duplicate rows and orphaned records. Green means production-safe."
        testid="admin-data-integrity-header"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none" onClick={load} disabled={loading}
                    data-testid="admin-data-integrity-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Rescan
            </Button>
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 pb-10 pt-6 space-y-4">
        {/* Overall status */}
        <div className="border border-border bg-card p-4 flex items-center gap-4" data-testid="admin-data-integrity-summary">
          <div className={`inline-flex items-center gap-2 px-3 py-2 border ${overall.cls}`}>
            <overall.icon className="w-5 h-5" />
            <span className="text-sm font-bold uppercase tracking-widest">{overall.label}</span>
          </div>
          <div className="grid grid-cols-3 gap-6 flex-1">
            <Kpi label="Live workspaces" value={data?.live_workspaces ?? "—"} />
            <Kpi label="Total orphans" value={totalOrphans} tone={totalOrphans ? "text-rose-700" : "text-emerald-700"} />
            <Kpi label="Duplicate rows" value={totalDupeRows} tone={totalDupeRows ? "text-amber-700" : "text-emerald-700"} />
          </div>
        </div>

        {/* Feb 2026 · Bulk Dedupe — clears historical duplicates on the 6
            collections whose unique indexes are blocked (workspace_settings,
            employees, bank_accounts, clients, brands, client_contacts).
            Winner per group = most-recent updated_at (fallback created_at,
            fallback highest _id). */}
        <div className="border border-border bg-card p-4 space-y-3" data-testid="admin-bulk-dedupe">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Bulk Dedupe</div>
              <div className="text-sm font-semibold mt-0.5">Clear historical duplicates on unique-index-blocked collections</div>
              <div className="text-xs text-muted-foreground mt-0.5">Runs on: workspace_settings · employees · bank_accounts · clients · brands · client_contacts. Idempotent — safe to re-run.</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                className="rounded-none"
                onClick={loadDedupePreview}
                disabled={dedupeLoading || dedupeRunning}
                data-testid="admin-bulk-dedupe-preview-btn"
              >
                <ScanSearch className={`w-4 h-4 mr-2 ${dedupeLoading ? "animate-pulse" : ""}`} />
                {dedupeLoading ? "Scanning…" : "Preview plan"}
              </Button>
              <Button
                className="rounded-none bg-rose-700 hover:bg-rose-800 text-white"
                onClick={executeDedupe}
                disabled={!dedupePreview || dedupeRunning || (dedupePreview?.total_rows_to_remove || 0) === 0}
                data-testid="admin-bulk-dedupe-execute-btn"
              >
                <Trash2 className={`w-4 h-4 mr-2 ${dedupeRunning ? "animate-pulse" : ""}`} />
                {dedupeRunning ? "Removing…" : `Execute${dedupePreview ? ` · ${(dedupePreview.total_rows_to_remove || 0).toLocaleString()} rows` : ""}`}
              </Button>
            </div>
          </div>

          {dedupePreview && (
            <div className="border border-border bg-muted/20" data-testid="admin-bulk-dedupe-preview">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Collection</th>
                    <th className="text-right px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Groups</th>
                    <th className="text-right px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Rows to remove</th>
                    <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Sample group</th>
                  </tr>
                </thead>
                <tbody>
                  {(dedupePreview.collections || []).map((c) => (
                    <tr key={c.collection} className="border-b border-border last:border-0"
                          data-testid={`admin-bulk-dedupe-row-${c.collection}`}>
                      <td className="px-3 py-1.5 font-mono text-xs">{c.collection}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{c.group_count ?? 0}</td>
                      <td className={`px-3 py-1.5 text-right font-mono ${(c.rows_to_remove || 0) > 0 ? "text-amber-800 font-bold" : "text-muted-foreground"}`}>
                        {(c.rows_to_remove || 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-1.5 text-[11px] text-muted-foreground truncate max-w-[440px]">
                        {c.error ? <span className="text-rose-700">error: {c.error}</span>
                          : (c.groups?.[0]
                            ? `keeps ${String(c.groups[0].keep_id).slice(-6)} · removes ${c.groups[0].remove_ids.length} peer(s)`
                            : "—")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {dedupeResult && (
            <div className="border border-emerald-300 bg-emerald-50 p-3 text-sm"
                    data-testid="admin-bulk-dedupe-result">
              <div className="font-bold text-emerald-800 mb-1">Removed {(dedupeResult.total_removed || 0).toLocaleString()} row(s)</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-0.5 text-xs text-emerald-900">
                {(dedupeResult.collections || []).map((c) => (
                  <div key={c.collection} className="font-mono">
                    {c.collection}: {c.error ? <span className="text-rose-700">error</span> : (c.removed ?? 0)}
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-emerald-900 mt-2">
                Restart the backend so the previously-blocked unique indexes can build (`sudo supervisorctl restart backend` or redeploy).
              </div>
            </div>
          )}
        </div>

        {/* Per-collection table */}
        <div className="border border-border bg-card overflow-hidden" data-testid="admin-data-integrity-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Collection</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Orphans</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Dupe groups</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Dupe rows</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Status</th>
                <th className="px-3 py-2 border-b border-border" />
              </tr>
            </thead>
            <tbody>
              {collectionNames.map((coll) => {
                const c = checks[coll];
                const st = STATUS[statusOf(c)];
                const isOpen = expanded === coll;
                const canDedupe = coll === "finance_accounts";
                return (
                  <>
                    <tr key={coll} className={`border-b border-border ${isOpen ? "bg-primary/5" : ""}`}
                          data-testid={`admin-data-integrity-row-${coll}`}>
                      <td className="px-3 py-2 font-mono text-xs">{coll}</td>
                      <td className="px-3 py-2 text-right font-mono">{c.orphans ?? 0}</td>
                      <td className="px-3 py-2 text-right font-mono">{c.dupe_group_count ?? 0}</td>
                      <td className="px-3 py-2 text-right font-mono">{c.dupe_row_count ?? 0}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase border px-1.5 py-0.5 ${st.cls}`}>
                          <st.icon className="w-3 h-3" /> {st.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {(c.dupe_group_count || 0) > 0 && (
                          <Button variant="ghost" size="sm" className="rounded-none h-7 text-xs"
                                  onClick={() => setExpanded(isOpen ? null : coll)}
                                  data-testid={`admin-data-integrity-expand-${coll}`}>
                            <ChevronRight className={`w-3 h-3 mr-1 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                            {isOpen ? "Hide" : "Show groups"}
                          </Button>
                        )}
                      </td>
                    </tr>
                    {isOpen && (c.dupes || []).length > 0 && (
                      <tr>
                        <td colSpan={6} className="bg-muted/20 p-3">
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
                            Duplicate groups (top {c.dupes.length})
                          </div>
                          <div className="space-y-1">
                            {c.dupes.map((g, i) => {
                              const wsId = g._id?.workspace_id;
                              const key = Object.entries(g._id || {}).filter(([k]) => k !== "workspace_id").map(([k, v]) => `${k}=${v}`).join(", ");
                              return (
                                <div key={i} className="flex items-center gap-2 border border-border bg-card px-2 py-1"
                                       data-testid={`admin-data-integrity-dupe-${coll}-${i}`}>
                                  <span className="text-[10px] text-muted-foreground">ws:</span>
                                  <span className="font-mono text-[11px]">{wsId?.slice(0, 12)}…</span>
                                  <span className="text-[11px] flex-1 truncate">{key}</span>
                                  <Badge variant="outline" className="rounded-none text-[9px]">×{g.n}</Badge>
                                  {canDedupe && (
                                    <Button variant="outline" size="sm" className="rounded-none h-6 text-[10px]"
                                            onClick={() => runFinanceDedupe(wsId, g._id?.code)}
                                            disabled={busyKey === `dedupe-${wsId}`}
                                            data-testid={`admin-data-integrity-dedupe-${wsId}`}>
                                      <Wrench className="w-3 h-3 mr-1" /> Dedupe
                                    </Button>
                                  )}
                                  <Button variant="outline" size="sm" className="rounded-none h-6 text-[10px] border-rose-500/50 text-rose-700 hover:bg-rose-50"
                                          onClick={() => resetWorkspace(wsId, true)}
                                          disabled={busyKey === `reset-${wsId}`}
                                          data-testid={`admin-data-integrity-reset-${wsId}`}>
                                    <RotateCcw className="w-3 h-3 mr-1" /> Reset ws
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
              {collectionNames.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted-foreground text-sm py-8">
                    {loading ? "Scanning…" : "No checks returned"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {data?.generated_at && (
          <div className="text-[10px] text-muted-foreground text-right">
            Generated {new Date(data.generated_at).toLocaleString()}
          </div>
        )}
      </div>
    </>
  );
}

function Kpi({ label, value, tone = "" }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className={`text-2xl font-mono font-black mt-1 ${tone}`}>{value}</div>
    </div>
  );
}
